/**
 * Step 1: Source Selection
 *
 * Select import source: Upload Document or Import from URL
 */

import React from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
} from "@mui/material";
import { UploadFile, Language } from "@mui/icons-material";
import type { ImportMethod } from "./JobImportWizard";

interface Props {
  onNext: () => void;
  updateState: (
    updates: Partial<import("./JobImportWizard").JobImportWizardState>,
  ) => void;
}

const Step1_SourceSelection: React.FC<Props> = ({ onNext, updateState }) => {
  const { t } = useTranslation(["recruiting"]);

  const handleSelect = (method: ImportMethod) => {
    updateState({ importMethod: method });
    onNext();
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {t("jobImport.step1.title", "How would you like to import?")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t(
          "jobImport.step1.subtitle",
          "Choose your import source to get started.",
        )}
      </Typography>

      <Box sx={{ display: "flex", gap: 3 }}>
        <Card sx={{ maxWidth: 400, flex: 1 }}>
          <CardActionArea
            onClick={() => handleSelect("document")}
            data-testid="source-document"
            sx={{ transition: "all 0.2s", height: "100%" }}
          >
            <CardContent sx={{ textAlign: "center", py: 4 }}>
              <UploadFile sx={{ fontSize: 64, color: "primary.main", mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {t("jobImport.step1.documentTitle", "Upload Document")}
              </Typography>
              <Typography color="text.secondary">
                {t(
                  "jobImport.step1.documentDescription",
                  "Upload a PDF, DOC, or DOCX file containing a job description.",
                )}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>

        <Card sx={{ maxWidth: 400, flex: 1 }}>
          <CardActionArea
            onClick={() => handleSelect("url")}
            data-testid="source-url"
            sx={{ transition: "all 0.2s", height: "100%" }}
          >
            <CardContent sx={{ textAlign: "center", py: 4 }}>
              <Language sx={{ fontSize: 64, color: "primary.main", mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {t("jobImport.step1.urlTitle", "Import from URL")}
              </Typography>
              <Typography color="text.secondary">
                {t(
                  "jobImport.step1.urlDescription",
                  "Paste a link to a job posting page and we'll extract the details.",
                )}
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      </Box>
    </Box>
  );
};

export default Step1_SourceSelection;
